import { Modal } from 'ant-design-vue';
import { reactive, ref } from 'vue';
import { apiRoutes } from '../infraestructure/apiRoutes';
import { buildQueryParameters, get, post, softDelete, update } from '../infraestructure/repository/generic.repository';
import useBusinessUnit from "./useBusinessUnit";

const usePlant = () => {

  const {
    selectBusinessUnits,
    getBusinessUnitsNoPaginate,
  } = useBusinessUnit()

  // ****************************************************************
  // ****************************************************************
  // VARIABLES INITIALIZATION

  // Paginator constants
  let paginatorTotalItems = ref(0)
  let paginatorTotalPages = ref(0)
  let paginatorCurrentPage = ref(0)

  const searchText = ref('')
  const visible = ref(false)

  const formId = ref('')
  let form = reactive({
    code: '',
    name: '',
    businessUnit: '',
  })
  let formLoad = ref(false)
  const rules = {
    code: [{ required: true, message: 'El código de planta es obligatorio' }],
    name: [{ required: true, message: 'El nombre de planta es obligatorio' }],
    businessUnit: [{ required: true, message: 'La unidad de negocio es obligatoria' }],
  }

  // ERROR VARIABLES
  let errorHandler = reactive({
    errorDescription: {
      errors: [],
    },
    errorNumber: 0,
  })
  const ruleForm = ref(null)

  const plants = ref([])
  const selectPlants = ref([])
  const tableLoading = ref(false)
  // VARIABLES INITIALIZATION
  // ****************************************************************
  // ****************************************************************

  // ****************************************************************
  // ****************************************************************
  // MODAL FUNCTIONS
  const openModal = async (data = null) => {
    tableLoading.value = true
    visible.value = true
    await getBusinessUnitsNoPaginate()
    if (formId.value !== '' && data) {
      const { code, name, businessUnit } = data
      form.code = code
      form.name = name
      form.businessUnit = businessUnit.id
    }
    tableLoading.value = false
  }
  const closeModal = () => {
    visible.value = false
    reinitializeForm()
  }
  const reinitializeForm = () => {
    formId.value = ''
    form.code = ''
    form.name = ''
    form.businessUnit = ''

    errorHandler.errorDescription.errors = []
    errorHandler.errorNumber = 0
  }
  // MODAL FUNCTIONS
  // ****************************************************************
  // ****************************************************************

  // ****************************************************************
  // ****************************************************************
  // EVENTS FUNCTIONS
  const eventOpenModal = async (data) => {
    const { id } = data
    formId.value = id
    await openModal(data)
  }

  const eventSoftDelete = (data) => {
    Modal.confirm({
      title: '¿Está seguro de eliminar este registro?',
      content: 'Está operación será irreversible, elija con precaución.',
      okText: 'Si, eliminar',
      okType: 'danger',
      cancelText: 'No',
      async onOk() {
        tableLoading.value = true

        const { id } = data
        formId.value = id
        await deleteRecord()
      },
      onCancel() {
      },
    })
  }
  // EVENTS FUNCTIONS
  // ****************************************************************
  // ****************************************************************

  const getPlantsNoPaginate = async () => {
    const path = `${apiRoutes.plant}`
    const response = await get(path)
    const { items } = response.data
    selectPlants.value = items
  }

  const getPlantsNoPaginateByBusinessUnit = async (businessUnit) => {
    const path = `${apiRoutes.plant}/${businessUnit}`
    const response = await get(path)
    const { items } = response.data
    return items
  }

  const onChangePaginator = async (current) => {
    await getPlants(10, (current - 1))
  }

  const getPlants = async (limit = 10, page = 0) => {
    tableLoading.value = true

    const path = `${apiRoutes.plant}${buildQueryParameters(searchText, limit, page)}`
    const response = await get(path)
    const { total, items, totalPages, currentPage } = response.data

    plants.value = items
    paginatorTotalItems.value = total
    paginatorTotalPages.value = totalPages
    paginatorCurrentPage.value = currentPage

    tableLoading.value = false
  }
  getPlants(10, 0)

  const submit = async () => {
    formLoad.value = true
    const path = apiRoutes.plant

    if (formId.value !== '') {

      // Update
      await update(path, formId.value, form).then(() => {

        Modal.success({
          title: `¡El registro se ha actualizado exitosamente!`,
        })
        closeModal()
        formLoad.value = false

      }).catch((error) => {
        const res = error?.response;
        const data = res?.data;
        let msgs = [];

        if (error?.message && !res) {
          msgs = [String(error.message)];
        } else if (Array.isArray(data?.message)) {
          msgs = data.message.map(m => String(m)).filter(Boolean);
        } else if (typeof data?.message === 'string') {
          msgs = [data.message];
        } else if (typeof data === 'string') {
          msgs = [data];
        }

        if (!msgs.length) {
          const raw = error?.message || 'Ocurrió un error al actualizar la planta.';
          msgs = [raw];
        }

        errorHandler.errorDescription.errors = msgs;
        errorHandler.errorNumber = data?.statusCode || res?.status || 0;
        formLoad.value = false;
      })

    } else {

      // Create
      await post(path, form).then(() => {

        Modal.success({
          title: `¡El registro se ha almacenado exitosamente!`,
        })
        closeModal()
        formLoad.value = false

      }).catch((error) => {
        const res = error?.response;
        const data = res?.data;
        let msgs = [];

        if (error?.message && !res) {
          // Error already normalized (without response)
          msgs = [String(error.message)];
        } else if (Array.isArray(data?.message)) {
          msgs = data.message.map(m => String(m)).filter(Boolean);
        } else if (typeof data?.message === 'string') {
          msgs = [data.message];
        } else if (typeof data === 'string') {
          msgs = [data];
        }

        if (!msgs.length) {
          const raw = error?.message || 'Ocurrió un error al actualizar la planta.';
          msgs = [raw];
        }

        errorHandler.errorDescription.errors = msgs;
        errorHandler.errorNumber = data?.statusCode || res?.status || 0;
        formLoad.value = false;
      })

    }

    await getPlants(10, 0)
  }

  const deleteRecord = async () => {

    const path = apiRoutes.plant
    const response = await softDelete(path, formId.value)
    if (response) {
      formId.value = ''
      await getPlants(10, 0)
    }

  }

  const handleChange = (selectedItem) => {
    form.businessUnit = selectedItem
  }

  return {
    // ***************************************
    // SELECT DATA
    selectPlants,
    selectBusinessUnits,
    getPlantsNoPaginate,
    getPlantsNoPaginateByBusinessUnit,
    // SELECT DATA
    // ***************************************

    searchText,
    visible,
    formId,
    form,
    formLoad,
    rules,
    plants,
    tableLoading,
    errorHandler,
    ruleForm,

    paginatorTotalItems,
    paginatorTotalPages,
    paginatorCurrentPage,
    onChangePaginator,

    openModal,
    closeModal,
    getPlants,
    submit,
    deleteRecord,
    handleChange,

    eventOpenModal,
    eventSoftDelete,
  }
}

export default usePlant

